/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";
import AutoForm from "../form/functional/AutoForm";
import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import { cloneDeep, debounce } from "lodash";
import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import CustomSelector from "../input/CustomSelector";

import layoutConf from "../../config/layoutConf";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import failureKey from "../../lib/common/failureKey";

/**
 * @COMPONENT_NAME CopyBaseCardColorDialog
 * @DESCRIPTION 기준 카드 색상 복사 다이얼로그. 기준 색상과 전/후면을 선택하고, 선택된 행의 정보를 복사 등록할 수 있는 폼과 그리드 복합 컴포넌트입니다.
 *
 * @PROPS
 *   - {string} id - 다이얼로그 고유 ID
 *   - {object} selectedCardClass - 선택된 카드 분류 객체 (classCode 등)
 *   - {object} selectedEndUser - 선택된 사용자 객체 (endUserCode 등)
 *   - {boolean} moveable - 다이얼로그 이동 가능 여부
 *   - {boolean} resizable - 다이얼로그 리사이즈 가능 여부
 *   - {boolean} fullWidth - 다이얼로그 전체 너비 사용 여부
 *   - {string|boolean} maxWidth - 다이얼로그 최대 너비
 *   - {function} onClose - 다이얼로그 닫기 콜백
 *   - {function} onInitialized - 다이얼로그 초기화 콜백
 *   - {function} onSuccess - 복사 성공 시 콜백
 *   - {object} schema - 폼/그리드에 사용될 스키마 정보
 *   - 기타 Dialog, AutoForm, BasicCrudScreenType 등에서 사용하는 props
 *
 * @STATE
 *   - {object} formData - 폼 입력값
 *   - {object} baseCardColor - 복사할 기준 카드 색상 정보
 *   - {array} apiArray - 기준 색상 선택용 배열
 *   - {array} frontRearArray - 전/후면 선택용 배열
 *   - {string} selectedTargetName - 선택된 기준 색상명
 *   - {string} selectedFrontRearName - 선택된 전/후면명
 *   - {object} notification - 알림 상태
 *   - {object} modifySchema - 폼/그리드용 수정 스키마
 *
 * @EXAMPLE
 *   <CopyBaseCardColorDialog
 *     id="copyDialog"
 *     selectedCardClass={...}
 *     selectedEndUser={...}
 *     onClose={...}
 *     onSuccess={...}
 *     schema={...}
 *   />
 *
 * @DESCRIPTION
 *   - 기준 색상과 전/후면을 선택하면, 해당 조건에 맞는 BaseCardColor 목록이 그리드에 표시됩니다.
 *   - 그리드에서 행을 선택 후 복사 버튼을 누르면, 해당 행의 정보를 StandardCardColor로 복사 등록합니다.
 *   - 폼 입력값과 그리드 선택값을 합쳐서 복사 API로 전송합니다.
 */

function CopyBaseCardColorDialog(props) {
	const { t } = useTranslation();
	const [formData, setFormData] = useState({});
	const [instance, setInstance] = useState();
	const [baseCardColor, setBaseCardColor] = useState();
	const [api, setApi] = useState();
	const [grid, setGrid] = useState();
	const [apiArray, setApiArray] = useState([]);
	const [selectedTargetName, setSelectedTargetName] = useState();
	const [frontRearArray, setFrontRearArray] = useState([]);
	const [selectedFrontRearName, setSelectedFrontRearName] = useState();
	const [notification, setNotification] = useState({
		open: false,
		severity: "success",
		msg: "",
	});
	const [modifySchema, setModifySchema] = useState();
	const {
		id,
		selectedCardClass,
		selectedEndUser,
		moveable,
		resizable,
		fullWidth,
		maxWidth,
		onClose,
		onInitialized,
		onSuccess,
		schema,
		...others
	} = props;

	useEffect(() => {
		(async () => {
			let response = await apiClient.get("/api/VwCommonCodeSlave/P061");
			const list = response?.data?.data;
			if (Array.isArray(list)) {
				// [{ name, code }] 형태로 변환
				const selected = list.map((item) => ({
					name: item?.slaveCodeName,
					code: item?.slaveCodeId,
				}));
				setFrontRearArray(selected);
				setSelectedFrontRearName(selected[0]?.name);
			} else {
				setFrontRearArray([]);
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			let response = await apiClient.get("/api/VwStaticColor");
			const list = response?.data?.data;
			if (Array.isArray(list)) {
				// [{ name, code }] 형태로 변환
				const selected = list.map((item) => ({
					name: item?.staticColorName,
					code: item?.staticColorCode,
				}));
				setApiArray(selected);
				setSelectedTargetName(selected[0]?.name);
			} else {
				setApiArray([]);
			}
		})();
	}, []);
	useEffect(() => {
		let modifiedSchema = cloneDeep(schema);

		if (modifiedSchema) {
			for (const column of modifiedSchema.columns) {
				switch (column.name) {
					case "frontRearCode":
						column.hideInForm = true;
						break;

					case "procSeq":
						column.hideInForm = true;
						break;

					case "processCode":
						column.hideInForm = true;
						break;

					case "colorCode":
						column.hideInForm = true;
						break;

					case "lossFlag":
						column.required = false;
						column.hideInForm = true;
						break;

					default:
						break;
				}
			}

			setModifySchema(modifiedSchema);
		}
	}, [schema]);

	useEffect(() => {
		(async () => {
			if (grid) {
				grid.selectionChanged.addHandler(
					debounce((grid, event) => {
						if (grid.selectedItems && grid.selectedItems[0]) {
							setBaseCardColor({
								standardFrontRearCode:
									grid.selectedItems[0].frontRearCode,
								standardProcessCode:
									grid.selectedItems[0].processCode,
								standardColorCode:
									grid.selectedItems[0].colorCode,
								standardLossFlag:
									grid.selectedItems[0].lossFlag,
							});
						} else {
							setBaseCardColor();
						}
					}, 10)
				);

				grid.itemsSourceChanged.addHandler(
					debounce((grid, event) => {
						if (grid.selectedItems && grid.selectedItems[0]) {
							setBaseCardColor({
								standardFrontRearCode:
									grid.selectedItems[0].frontRearCode,
								standardProcessCode:
									grid.selectedItems[0].processCode,
								standardColorCode:
									grid.selectedItems[0].colorCode,
								standardLossFlag:
									grid.selectedItems[0].lossFlag,
							});
						} else {
							setBaseCardColor();
						}
					}, 10)
				);
			}
		})();
	}, [grid]);

	useEffect(() => {
		if (api) api.refresh();
	}, [selectedTargetName, selectedFrontRearName]);

	const notifyFailure = (msg) => {
		console.error(msg);

		setNotification({
			severity: "error",
			msg: msg,
			open: true,
		});
	};

	const onDialogInitialized = (instance) => {
		setInstance(instance);

		if (onInitialized) {
			onInitialized(instance);
		}
	};

	const onApiSuccess = (result) => {
		if (onSuccess) {
			setTimeout(onSuccess(result.data), 0);

			setInstance((instance) => {
				instance.hide();
				return instance;
			});
		}
	};

	const onApiFailure = (reason) => {
		notifyFailure(
			t(failureKey("insert", reason.response?.status), {
				reason: reason.response
					? reason.response.data
					: JSON.stringify(reason),
			})
		);

		setInstance((instance) => {
			instance.hide();
			return instance;
		});
	};

	const onOk = (baseCardColor, formData) => {
		// build base api url

		let url = `/api/StandardCardColor/copy`;

		let apiData = {
			...baseCardColor,
			...formData,
		};

		let form = new FormData();

		let json = {};
		let deleted = {};

		for (const formItem in apiData) {
			if (apiData[formItem] !== undefined) {
				json[formItem] = apiData[formItem];
			}
		}

		form.append("deleted", JSON.stringify(deleted));
		form.append("json", JSON.stringify(json));

		apiClient
			.post(url, form, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			})
			.then((result) => onApiSuccess(result))
			.catch((reason) => onApiFailure(reason));
	};

	const onCancel = () => {
		instance.hide();
	};

	const onCloseNotification = () => {
		setNotification((notification) => {
			return { ...notification, open: false };
		});
	};

	const onCloseDialog = () => {
		if (onClose) {
			onClose();
		}
	};
	const setInitialDataCallback = (formData) => {
		formData["cardClass"] = selectedCardClass?.classCode;
		formData["cardUserCd"] = selectedEndUser?.endUserCode;
	};
	const onChange = (formData) => {
		setFormData(formData);
	};

	const onModifyViewUrl = (url) => {
		const selected = apiArray.find(
			(item) => item.name === selectedTargetName
		);
		const frontRearSelected = frontRearArray.find(
			(item) => item.name === selectedFrontRearName
		);
		const code = selected?.code;
		const frontRearCode = frontRearSelected?.code;
		let newUrl = `${url}/${code}/${frontRearCode}`;
		return newUrl;
	};
	const onApiInitialized = (api) => {
		setApi(api);
	};
	const onGridInitialized = (grid) => {
		setGrid(grid);
	};

	return (
		<Dialog
			id={id}
			moveable={moveable ? moveable : uiConf.dialogMoveable}
			resizable={resizable ? resizable : uiConf.dialogResizable}
			fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
			maxWidth="lg"
			onClose={onCloseDialog}
			onInitialized={onDialogInitialized}
			{...others}
		>
			<DialogHeader>
				<DialogTitle>{t("dialog.title.CopyBaseCardColor")}</DialogTitle>
			</DialogHeader>

			<Divider />

			<DialogContent style={{ padding: "0px", height: "600px" }}>
				<ReflexContainer orientation="horizontal">
					<ReflexElement flex={0.2} data-cy="StandardCardColor">
						<AutoForm
							id="StandardCardColor"
							schema={modifySchema}
							mode="create"
							onChange={onChange}
							// initialData={formData}
							setInitialDataCallback={setInitialDataCallback}
						/>
					</ReflexElement>
					<ReflexSplitter
						style={{ height: layoutConf.reflex.splitterHeight }}
					/>

					<ReflexElement flex={0.8} data-cy="BaseCardColor">
						<BasicCrudScreenType
							id="BaseCardColor"
							headerTitle={t("term.baseCardColor")}
							table="BaseCardColor"
							view="VwBaseCardColor"
							noCreate
							noEdit
							noDelete
							noExcel
							onInitialized={onGridInitialized}
							onApiInitialized={onApiInitialized}
							onModifyViewUrl={onModifyViewUrl}
							// onModifyViewSchema={onModifyViewSchema}
							onAppendButtonInFrontOfSearch={() => (
								<>
									<BasicCrudScreenActionItemLayout>
										<CustomSelector
											value={selectedFrontRearName}
											setValue={setSelectedFrontRearName}
											apiArray={frontRearArray.map(
												(item) => item.name
											)}
											label={"전/후면"}
										/>
									</BasicCrudScreenActionItemLayout>
									<BasicCrudScreenActionItemLayout>
										<CustomSelector
											value={selectedTargetName}
											setValue={setSelectedTargetName}
											apiArray={apiArray.map(
												(item) => item.name
											)}
											label={"기준 색상"}
										/>
									</BasicCrudScreenActionItemLayout>
								</>
							)}
						/>
					</ReflexElement>
				</ReflexContainer>

				<Snackbar
					open={
						notification.open && notification.severity !== "success"
					}
					autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
					onClose={onCloseNotification}
				>
					<Alert
						onClose={onCloseNotification}
						variant="filled"
						severity={notification.severity}
					>
						{notification.msg}
					</Alert>
				</Snackbar>
			</DialogContent>

			<Divider />

			<DialogActionsOkCancel
				labels={[t("buttonName.copy")]}
				buttonDisabled={[!baseCardColor, false]}
				onOk={() => onOk(baseCardColor, formData)}
				onCancel={onCancel}
			/>
		</Dialog>
	);
}

export default CopyBaseCardColorDialog;
